import { ChangeDetectionStrategy, Component, inject, OnInit, signal } from '@angular/core';
import { DatePipe } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';

import {
  ButtonComponent,
  CellComponent,
  DialogService,
  FilterTablePageBaseComponent,
  IconComponent,
  RowDirective,
  TableComponent,
  TableMobileComponent,
  TableWrapperComponent,
} from '@rp/shared/components';
import {
  ClipboardService,
  CommonMediaQueries,
  HttpErrorHandlerService,
  MediaScreenService,
} from '@rp/shared/services';
import { TranslateModule } from '@ngx-translate/core';
import { filter, finalize, Subject } from 'rxjs';
import { DateFormat, Entity, SortingRequest, StructureResponse } from '@rp/shared/models';
import { CentsToDollarsPipe } from '@rp/shared/pipes';
import { StructuresService } from '@rp/core';

import {
  BalanceCardsComponent,
  FinanceStatusComponent,
  DetailsDialogComponent,
  FinanceMobileViewComponent,
  FiltersWebmasterComponent,
} from './components';
import { FINANCE_WEBMASTER_PROVIDER_TOKEN, HttpFinanceWebmasterProvider } from './providers';
import { Payout, PayoutResponse, PayoutWebmasterFilter } from './models';
import { WEBMASTER_FINANCE_TABLE_COLUMNS } from './consts/webmaster-finance-table-columns';
import { WebmasterFiltersAdapter } from './adapters';

@Component({
  selector: 'rp-finance',
  standalone: true,
  templateUrl: './finance.component.html',
  styleUrl: './finance.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CellComponent,
    RowDirective,
    TableComponent,
    ButtonComponent,
    ReactiveFormsModule,
    BalanceCardsComponent,
    TranslateModule,
    FinanceStatusComponent,
    IconComponent,
    TableMobileComponent,
    FiltersWebmasterComponent,
    TableWrapperComponent,
    DatePipe,
    CentsToDollarsPipe,
    FinanceMobileViewComponent,
    RouterLink,
  ],
  providers: [
    { provide: FINANCE_WEBMASTER_PROVIDER_TOKEN, useClass: HttpFinanceWebmasterProvider },
    DatePipe,
  ],
})
export class FinanceComponent
  extends FilterTablePageBaseComponent<PayoutWebmasterFilter>
  implements OnInit
{
  columns = WEBMASTER_FINANCE_TABLE_COLUMNS;
  DateFormat = DateFormat;

  payouts = signal<PayoutResponse>({
    itemsCount: 0,
    payouts: [],
  });
  structures = signal<StructureResponse>(null);
  paymentMethods = signal<Map<number, string>>(null);
  paymentNetworks = signal<Map<number, string>>(null);
  payoutStatuses = signal<Map<number, string>>(null);

  override sorting = signal<SortingRequest>({ sorting: 'amount' });

  clipboardService = inject(ClipboardService);
  private _dialogService = inject(DialogService);
  private _financeWebmasterProvider = inject(FINANCE_WEBMASTER_PROVIDER_TOKEN);
  private _httpErrorHandlerService = inject(HttpErrorHandlerService);
  private _mediaScreenService = inject(MediaScreenService);
  private _router = inject(Router);
  private _structuresService = inject(StructuresService);

  // eslint-disable-next-line @typescript-eslint/member-ordering
  isDesktop = toSignal(this._mediaScreenService.mediaMatcher(CommonMediaQueries.MD));
  // eslint-disable-next-line @typescript-eslint/member-ordering
  override adapter = WebmasterFiltersAdapter;

  ngOnInit(): void {
    this._getStructures();

    this._getData();
  }

  handleGetPayouts(): void {
    this._getData();
  }

  openDetails(payout: Payout): void {
    if (!this._mediaScreenService.isMatched(CommonMediaQueries.MD)) {
      this._router.navigate(['finance', 'view'], {
        queryParams: {
          ...payout,
          paymentMethodId: this.paymentMethods().get(payout.paymentMethodId),
          networkId: this.paymentNetworks().get(payout.networkId),
        },
      });
    } else {
      const shouldUpdatePayoutsSubject = new Subject();

      this._dialogService.open(DetailsDialogComponent, {
        statuses: this.payoutStatuses,
        structures: this.structures,
        payout,
        shouldUpdatePayoutsSubject,
      });

      if (this._mediaScreenService.isMatched(CommonMediaQueries.MD)) {
        shouldUpdatePayoutsSubject
          .pipe(filter(Boolean), takeUntilDestroyed(this._destroyRef))
          .subscribe(() => this._getData());
      }
    }
  }

  protected _getData(): void {
    this.isLoading.set(true);

    this._financeWebmasterProvider
      .getPayouts(this.params())
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        finalize(() => this.isLoading.set(false)),
      )
      .subscribe({
        next: payouts => this.payouts.set(payouts),
        error: err => this._httpErrorHandlerService.showErrorMessage(err),
      });
  }

  private _getStructures(): void {
    this._structuresService
      .getStructures()
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((structures: StructureResponse) => {
        const { paymentMethods, paymentNetworks, payoutStatuses } = structures;
        this.structures.set(structures);
        this.paymentMethods.set(
          new Map(
            paymentMethods.map((paymentMethod: Entity) => [paymentMethod.id, paymentMethod.name]),
          ),
        );
        this.paymentNetworks.set(
          new Map(
            paymentNetworks.map((paymentNetwork: Entity) => [
              paymentNetwork.id,
              paymentNetwork.name,
            ]),
          ),
        );
        this.payoutStatuses.set(
          new Map(
            payoutStatuses.map((payoutStatus: Entity) => [payoutStatus.id, payoutStatus.name]),
          ),
        );
      });
  }
}
